"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  MODAL_PANEL_ANIMATION_MS,
  type ModalMotionPhase,
} from "./modalMotion";

function initialPhase(isOpen: boolean): ModalMotionPhase {
  return isOpen ? "entering" : "idle";
}

type ModalMotionKind = "modal" | "sheet" | "drawer";

function isEnterAnimationComplete(
  kind: ModalMotionKind,
  animationName: string,
): boolean {
  if (kind === "sheet") {
    return animationName.includes("workspace-picker-sheet-in");
  }
  if (kind === "drawer") {
    return (
      animationName.includes("aera-drawer-right-in") ||
      animationName.includes("aera-drawer-sheet-in")
    );
  }
  return animationName.includes("bounce-in-down");
}

function isExitAnimationComplete(
  kind: ModalMotionKind,
  animationName: string,
): boolean {
  if (kind === "sheet") {
    return animationName.includes("workspace-picker-sheet-out");
  }
  if (kind === "drawer") {
    return (
      animationName.includes("aera-drawer-right-out") ||
      animationName.includes("aera-drawer-sheet-out")
    );
  }
  return animationName.includes("bounce-out-down");
}

export function useModalPresence(
  isOpen: boolean,
  durationMs = MODAL_PANEL_ANIMATION_MS,
  onExited?: () => void,
  motionKind: ModalMotionKind = "modal",
) {
  const [rendered, setRendered] = useState(isOpen);
  const [phase, setPhase] = useState<ModalMotionPhase>(() =>
    initialPhase(isOpen),
  );
  const onExitedRef = useRef(onExited);
  onExitedRef.current = onExited;
  const exitFinishedRef = useRef(false);

  const finishExit = useCallback(() => {
    if (exitFinishedRef.current) return;
    exitFinishedRef.current = true;
    setRendered(false);
    setPhase("idle");
    onExitedRef.current?.();
  }, []);

  useEffect(() => {
    if (isOpen) {
      exitFinishedRef.current = false;
      setRendered(true);
      setPhase((current) => {
        if (current === "open" || current === "entering") return current;
        return "entering";
      });
      return;
    }
    setPhase((current) => (current === "idle" ? "idle" : "exiting"));
  }, [isOpen]);

  const onPanelMotionEnd = useCallback(
    (e: React.AnimationEvent<HTMLElement>) => {
      if (e.target !== e.currentTarget) return;
      const { animationName } = e;

      if (
        phase === "entering" &&
        isEnterAnimationComplete(motionKind, animationName)
      ) {
        setPhase("open");
        return;
      }

      if (
        phase === "exiting" &&
        isExitAnimationComplete(motionKind, animationName)
      ) {
        finishExit();
      }
    },
    [phase, finishExit, motionKind],
  );

  useEffect(() => {
    if (!rendered || phase !== "exiting") return;
    const t = window.setTimeout(finishExit, durationMs + 80);
    return () => window.clearTimeout(t);
  }, [rendered, phase, durationMs, finishExit]);

  return { rendered, phase, onPanelMotionEnd };
}
